import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import { apiErrorMessage } from '@/lib/clients';

import type { Geofence, GeofenceMetadata } from '@logistech/ui-shared';

// Bornes alignées sur rules-engine (routes/geofences/geofence.shared.ts)
export const MIN_VERTICES = 3;
export const MAX_VERTICES = 500;
export const MIN_RADIUS_M = 50;
export const MAX_RADIUS_M = 50_000;
export const NAME_MAX = 100;

/** Couleur de marque par défaut d'une zone sans couleur propre. */
export const DEFAULT_GEOFENCE_COLOR = '#26215C';
export const SELECTED_OUTLINE_COLOR = '#F59E0B';

/** Guinée : vue initiale de la carte. */
export const GUINEA_VIEW = { latitude: 9.6, longitude: -13.6, zoom: 7 } as const;

// Aligné sur l'enum GeofenceCategory (prisma/schema.prisma)
export const CATEGORY_LABELS: Record<Geofence['category'], string> = {
  MINE: 'Mine',
  PORT: 'Port',
  WAREHOUSE: 'Entrepôt',
  CLIENT_SITE: 'Site client',
  RESTRICTED: 'Zone interdite',
  CITY: 'Ville',
  ROUTE_CORRIDOR: 'Corridor routier',
  CUSTOM: 'Personnalisée',
};

export type GeofenceShape = 'polygon' | 'circle';
export type PolygonGeom = Geofence['geom'];

/** Géométrie + metadata prêtes pour POST / PATCH. */
export interface GeofenceDraft {
  geom: PolygonGeom;
  metadata: GeofenceMetadata;
}

export const shapeOf = (g: Pick<Geofence, 'metadata'>): GeofenceShape => (g.metadata?.shape === 'circle' ? 'circle' : 'polygon');

/** Sommets de l'anneau extérieur, fermeture non comptée. */
export const vertexCount = (geom: PolygonGeom): number => Math.max((geom.coordinates[0]?.length ?? 1) - 1, 0);

/** Message d'erreur UI si le brouillon est hors des bornes acceptées par l'API, sinon null. */
export const draftError = (draft: GeofenceDraft): string | null => {
  if (draft.metadata.shape === 'circle') {
    const r = draft.metadata.radiusM ?? 0;
    if (r < MIN_RADIUS_M) return `Rayon trop petit (minimum ${MIN_RADIUS_M} m)`;
    if (r > MAX_RADIUS_M) return `Rayon trop grand (maximum ${MAX_RADIUS_M / 1000} km)`;
    return null;
  }
  const n = vertexCount(draft.geom);
  if (n < MIN_VERTICES) return `Au moins ${MIN_VERTICES} points sont nécessaires`;
  if (n > MAX_VERTICES) return `Trop de points (maximum ${MAX_VERTICES})`;
  return null;
};

interface DrawFeature {
  geometry: { type: string; coordinates: unknown };
  properties: Record<string, unknown>;
}

/**
 * Feature terra-draw (mode polygon ou circle) → brouillon API. Le centre d'un cercle est la moyenne de ses sommets
 * (anneau régulier) ; le rayon vient de properties.radiusKilometers.
 */
export const featureToDraft = (f: DrawFeature): GeofenceDraft => {
  const geom = { type: 'Polygon', coordinates: f.geometry.coordinates } as PolygonGeom;
  if (f.properties['mode'] !== 'circle') return { geom, metadata: { shape: 'polygon' } };
  const ring = geom.coordinates[0]!.slice(0, -1);
  const center: [number, number] = [
    round6(ring.reduce((s, p) => s + p[0], 0) / ring.length),
    round6(ring.reduce((s, p) => s + p[1], 0) / ring.length),
  ];
  const radiusM = Math.round(Number(f.properties['radiusKilometers'] ?? 0) * 1000);
  return { geom, metadata: { shape: 'circle', center, radiusM } };
};

const round6 = (n: number) => Math.round(n * 1e6) / 1e6;

/** Géofence → feature terra-draw éditable (le mode pilote les poignées : sommets ou redimensionnement du cercle). */
export const geofenceToFeature = (g: Geofence) => ({
  id: g.id,
  type: 'Feature' as const,
  geometry: g.geom,
  properties:
    shapeOf(g) === 'circle'
      ? { mode: 'circle', radiusKilometers: (g.metadata?.radiusM ?? 0) / 1000 }
      : { mode: 'polygon' },
});

/** [[minLon, minLat], [maxLon, maxLat]] de l'anneau extérieur. */
export const geomBounds = (geom: PolygonGeom): [[number, number], [number, number]] => {
  const ring = geom.coordinates[0] ?? [];
  const lons = ring.map((p) => p[0]);
  const lats = ring.map((p) => p[1]);
  return [[Math.min(...lons), Math.min(...lats)], [Math.max(...lons), Math.max(...lats)]];
};

export const fmtArea = (km2: number | null): string | null => {
  if (km2 == null) return null;
  return km2 < 1
    ? `${Math.round(km2 * 100).toLocaleString('fr-FR')} ha`
    : `${km2.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} km²`;
};

export const fmtRadius = (m: number): string =>
  m < 1000 ? `${m} m` : `${(m / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} km`;

/** Libellé court de la forme : « Cercle · 550 m » / « Polygone · 4 points ». */
export const shapeSummary = (g: Pick<Geofence, 'metadata' | 'geom'>): string =>
  shapeOf(g) === 'circle'
    ? `Cercle · ${fmtRadius(g.metadata?.radiusM ?? 0)}`
    : `Polygone · ${vertexCount(g.geom)} points`;

/** Code d'erreur métier renvoyé par l'API ({ error: { code } }). */
export const apiErrorCode = (err: unknown): string | undefined =>
  (err as { response?: { data?: { error?: { code?: string } } } })?.response?.data?.error?.code;

export const GEOFENCE_ERROR_MESSAGES: Record<string, string> = {
  DUPLICATE_NAME: 'Ce nom est déjà utilisé',
  INVALID_GEOMETRY: 'Géométrie invalide',
  INVALID_VERTEX_COUNT: `Le polygone doit compter entre ${MIN_VERTICES} et ${MAX_VERTICES} points`,
  INVALID_RADIUS: `Le rayon doit être compris entre ${MIN_RADIUS_M} m et ${MAX_RADIUS_M / 1000} km`,
  NOT_FOUND: 'Zone introuvable',
};

/** Toast d'une erreur API : message métier si le code est connu, sinon message serveur. */
export const toastApiError = (err: unknown, fallback: string) => {
  const code = apiErrorCode(err);
  toast.error((code && GEOFENCE_ERROR_MESSAGES[code]) ?? apiErrorMessage(err, fallback));
};

export interface CreateGeofenceBody extends GeofenceDraft {
  name: string;
  description?: string;
}

export const useCreateGeofence = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateGeofenceBody) => (await api.post<Geofence>('/geofences', body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['geofences'] }),
  });
};

export type UpdateGeofenceBody = Partial<GeofenceDraft> & { name?: string; description?: string | null };

export const useUpdateGeofence = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateGeofenceBody & { id: string }) => (await api.patch<Geofence>(`/geofences/${id}`, body)).data,
    // 404 compris : la zone a pu être supprimée ailleurs, la liste est relue
    onSettled: () => qc.invalidateQueries({ queryKey: ['geofences'] }),
  });
};

export const useDeleteGeofence = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/geofences/${id}`);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['geofences'] }),
  });
};
